import classnames from "classnames";
import { Link } from "react-router-dom";
import { MediaQuery } from "@outplayed/responsive";
import { getRiotAssetsContext, getS13RankUrl } from "@outplayed/riot-assets";
import { numberWithCommas } from "@ugg/shared/utils/math";
import { getTierColor, getProBuildRankName } from "@ugg/shared/utils/rank-helpers";
import { REGION_LABEL_MAP } from "../../leaderboard-filters";
import { LeaderboardPlayerPlus } from "@ugg/shared/interfaces/leaderboards.interface";

interface ProfileIconContainerProps {
  playerData: LeaderboardPlayerPlus;
  hideLevel?: boolean;
  small?: boolean;
}

const ProfileIconContainer = (props: ProfileIconContainerProps) => {
  const { getProfileIconImg } = getRiotAssetsContext();
  const { playerData, hideLevel, small } = props;
  const { summonerLevel, tier, iconId } = playerData || {};
  const tierColor = getTierColor(tier);

  return (
    <div
      className={classnames("profile-icon-border-container", { "profile-icon-border-container_small": small })}
      style={{ borderColor: tierColor }}
    >
      {!hideLevel && summonerLevel > 0 && (
        <div className="level-header" style={{ borderColor: tierColor }}>
          {summonerLevel}
        </div>
      )}
      <div className="profile-icon-border">
        {!hideLevel && summonerLevel > 0 && <div className="border-notch" />}
        <img className="profile-icon-image" src={getProfileIconImg(iconId || 1)} />
      </div>
    </div>
  );
};

interface RankDisplayProps {
  tier: string;
  rank: string;
  lp: number;
  className?: string;
}

const RankDisplay = (props: RankDisplayProps) => {
  const { tier, rank, lp, className } = props;

  return (
    <div className={classnames("rank-display", className)}>
      <div className="rank">
        <MediaQuery min="DESKTOP_SMALL" max="DESKTOP_LARGE">
          <strong>
            <span>{getProBuildRankName(tier, rank)}</span>
          </strong>
        </MediaQuery>
        <MediaQuery min="MOBILE_SMALL" max="TABLET">
          <strong style={{ color: getTierColor(tier) }}>
            <span>{getProBuildRankName(tier, rank, true)}</span>
            <span className="text-lavender-300"> / </span>
          </strong>
        </MediaQuery>
      </div>
      <div>{numberWithCommas(lp || 0)} LP</div>
    </div>
  );
};

interface WinRateBarProps {
  playerData: LeaderboardPlayerPlus;
  isMobile?: boolean;
  className?: string;
}

const WinRateBar = (props: WinRateBarProps) => {
  const { playerData, isMobile, className } = props;

  return (
    <div className={classnames("wr-with-bar", className)}>
      <div className="wr-text">
        <span className="wr-text_games">
          {numberWithCommas(Number(playerData.wins))}W {numberWithCommas(Number(playerData.losses))}L
        </span>
      </div>
      <div className="wr-text_win-rate">
        {playerData.winRate}% {isMobile ? "WR" : "Win Rate"}
      </div>
    </div>
  );
};

interface LeaderboardTopFiveProps {
  data: LeaderboardPlayerPlus[];
  topPlayerMostPlayedChamp: number;
  showRegion?: boolean;
}

const LeaderboardTopFive = (props: LeaderboardTopFiveProps) => {
  const { getChampionSplashImg } = getRiotAssetsContext();
  const { data, topPlayerMostPlayedChamp, showRegion } = props;

  const topPlayer = data[0];
  const remainingPlayers = data.slice(1);

  return (
    <div className="leaderboard_top-five">
      <div className="top-five_ranking top-five_ranking__1">
        <div className="champion-splash-bg">
          <img src={getChampionSplashImg(topPlayerMostPlayedChamp)} />
        </div>
        <div className="top-ranking-info-container top-ranking-info-container_mobile">
          <div className="ranking-label ranking-label_1">1</div>
          <div className="top-rank-info">
            <ProfileIconContainer playerData={topPlayer} />
            <div className="ranking-info">
              <div className="ranking-user-row">
                {showRegion && topPlayer.regionId && REGION_LABEL_MAP[topPlayer.regionId] && (
                  <div className="region-box">{REGION_LABEL_MAP[topPlayer.regionId]}</div>
                )}
                <Link to={topPlayer.profileURL} className="summoner-name">
                  {topPlayer.riotUserName}
                </Link>
                <div className="tag-line-1">#{topPlayer.riotTagLine}</div>
              </div>
              <div className="top-player-rank">
                <img className="rank-img" src={getS13RankUrl(topPlayer.tier)} />
                <RankDisplay tier={topPlayer.tier} rank={topPlayer.rank} lp={topPlayer.lp} className="rank-display_1" />
              </div>
            </div>
            <WinRateBar playerData={topPlayer} className="wr-with-bar_1" />
          </div>
        </div>
      </div>
      {remainingPlayers.map((player, index) => {
        return (
          <div key={index} className={`top-five_ranking top-five_ranking__other top-five_ranking__${index + 2}`}>
            <div className="ranking-info-container">
              <div className="ranking-header">
                <div className={`ranking-label ranking-label_${index + 2}`}>{index + 2}</div>
                <div className="ranking-player">
                  <ProfileIconContainer playerData={player} hideLevel small />
                  <div className="ranking-user-column">
                    <Link to={player.profileURL} className="summoner-name">
                      <div>{player.riotUserName}</div>
                    </Link>
                    <div className="tag-line">#{player.riotTagLine}</div>
                    {showRegion && player.regionId && REGION_LABEL_MAP[player.regionId] && (
                      <div className="region-box">{REGION_LABEL_MAP[player.regionId]}</div>
                    )}
                  </div>
                </div>
              </div>
              <div className="ranking-data-row">
                <img className="rank-img" src={getS13RankUrl(player.tier)} />
                <RankDisplay tier={player.tier} rank={player.rank} lp={player.lp} />
                <WinRateBar playerData={player} />
              </div>
            </div>
            <div className="ranking-info-container_mobile">
              <div className="player">
                <div className={`ranking-label ranking-label_${index + 2}`}>{index + 2}</div>
                <div className="ranking-player">
                  <ProfileIconContainer playerData={player} hideLevel small />
                  <Link to={player.profileURL} className="summoner-name">
                    {player.riotUserName}
                  </Link>
                </div>
              </div>
              <RankDisplay tier={player.tier} rank={player.rank} lp={player.lp} />
              <WinRateBar playerData={player} isMobile />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default LeaderboardTopFive;
